/**
 * Generic polled-fetch hook used by every OCPP tab.
 *
 * - Calls `fetcher` on mount.
 * - Optionally polls every `intervalMs` while `enabled` is true.
 * - Treats `intervalMs <= 0` (or omitted) as "no polling".
 * - Cleans up timers on unmount and on `chargerId` change.
 *
 * Returns `{ data, error, loading, refreshing, reload }`. Errors come pre-
 * mapped via `mapOcppApiError` so callers can render a banner straight away.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiResponse } from '../../services/api-requests';
import { isOcppSuccess, mapOcppApiError } from '../utils/apiErrors';
import type { OcppApiErrorInfo } from '../utils/apiErrors';

export interface UsePolledResourceOptions {
  /** Polling cadence. `0` or `undefined` means "no polling". */
  intervalMs?: number;
  /** Skip the initial fetch and skip polling when false. Defaults to true. */
  enabled?: boolean;
}

export interface UsePolledResourceResult<T> {
  data: T | null;
  error: OcppApiErrorInfo | null;
  loading: boolean;
  refreshing: boolean;
  reload: () => Promise<void>;
}

export function usePolledResource<T>(
  /**
   * Fetcher must be stable (memoised by the caller). The hook does not
   * try to deep-compare; if the fetcher changes the next call uses the
   * latest one.
   */
  fetcher: () => Promise<ApiResponse<T | null>>,
  options: UsePolledResourceOptions = {}
): UsePolledResourceResult<T> {
  const { intervalMs = 0, enabled = true } = options;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<OcppApiErrorInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const aliveRef = useRef(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(async (mode: 'initial' | 'refresh') => {
    if (mode === 'refresh') {
      setRefreshing(true);
    }
    try {
      const res = await fetcherRef.current();
      if (!aliveRef.current) return;
      if (isOcppSuccess(res.status)) {
        setData((res.payload ?? null) as T | null);
        setError(null);
      } else {
        setError(mapOcppApiError(res.status, res.payload));
      }
    } catch (e) {
      if (!aliveRef.current) return;
      setError(mapOcppApiError(undefined, { message: (e as Error)?.message }));
    } finally {
      if (!aliveRef.current) return;
      if (mode === 'initial') setLoading(false);
      if (mode === 'refresh') setRefreshing(false);
    }
  }, []);

  const reload = useCallback(async () => {
    await run('refresh');
  }, [run]);

  useEffect(() => {
    aliveRef.current = true;
    if (!enabled) {
      setLoading(false);
      return () => {
        aliveRef.current = false;
      };
    }
    setLoading(true);
    void run('initial');
    let id: ReturnType<typeof setInterval> | null = null;
    if (intervalMs && intervalMs > 0) {
      id = setInterval(() => {
        void run('refresh');
      }, intervalMs);
    }
    return () => {
      aliveRef.current = false;
      if (id) clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs]);

  return { data, error, loading, refreshing, reload };
}
